class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close()
    );
    this.querySelector("#CartDrawer-Overlay").addEventListener(
      "click",
      this.close.bind(this)
    );
    this.querySelector("#CartDrawer-ContinueShopping").addEventListener(
      "click",
      this.close.bind(this)
    );
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    if (!cartLink) return;

    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
      this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add("animate", "active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement =
          this.querySelector(".drawer__inner") ||
          this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add("overflow-hidden");
  }

  close() {
    this.classList.remove("active");
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden");
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute(
        "aria-controls",
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open")
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState) {
    //Check for CNR items before determining if cart is empty
    const checkCnrItems = this.checkForCnrItems();
    const isCartEmpty = parsedState.item_count === 0 && !checkCnrItems.hasCnrItems;
    parsedState.item_count += checkCnrItems.itemQuantity;

    // Only add 'is-empty' class if cart is truly empty (no regular items AND no CNR items)
    const drawerInner = this.querySelector(".drawer__inner");
    if (isCartEmpty) {
      drawerInner && drawerInner.classList.add("is-empty");
    } else {
      drawerInner && drawerInner.classList.remove("is-empty");
    }
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    setTimeout(() => {
      this.querySelector("#CartDrawer-Overlay").addEventListener(
        "click",
        this.close.bind(this)
      );
      this.open();

      this.querySelector("#CartDrawer-ContinueShopping").addEventListener(
        "click",
        this.close.bind(this)
      );
    });
  }

  checkForCnrItems() {
    const cnrItems = document.querySelectorAll('#cnr-cart-items tbody tr.cnr__cart-item');
    const cnrItemQuantities = Array.from(cnrItems).map(item => item.querySelector('.cnr_quantity__input'));
    const itemQuantity = Array.from(cnrItemQuantities).reduce((total, input) => {
      if (!input) return total;
      const quantity = parseInt(input.value, 10);
      return total + (isNaN(quantity) ? 0 : quantity);
    }, 0);
    return {
      hasCnrItems: cnrItems.length > 0,
      itemQuantity: itemQuantity,
    };
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    // Check if cart is truly empty (no regular items AND no CNR items)
    const cnrCartItems = this.checkForCnrItems();
    const cartItemCount = document.querySelector('cart-drawer-items')?.querySelectorAll('.cart-item:not(.cnr__cart-item)').length || 0;
    const isTrulyEmpty = cartItemCount <= 1 && !cnrCartItems.hasCnrItems;
    return [
      {
        id: "cart-drawer",
        selector: isTrulyEmpty ? ".drawer__inner" : "#CartDrawer-CartItems",
      },
      {
        id: "cart-icon-bubble",
      },
      {
        id: "cart-drawer",
        selector: ".drawer__header",
      },
      {
        id: "cart-drawer",
        selector: ".drawer__footer",
      },
      {
        id: "cart-drawer",
        selector: ".cart-drawer__form-wrapper",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    // Check if cart is truly empty (no regular items AND no CNR items)
    const cnrCartItems = this.checkForCnrItems();
    const cartItemCount = document.querySelector('cart-drawer-items')?.querySelectorAll('.cart-item:not(.cnr__cart-item)').length || 0;
    const isTrulyEmpty = cartItemCount <= 1 && !cnrCartItems.hasCnrItems;
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: isTrulyEmpty ? ".drawer__inner" : "#CartDrawer-CartItems",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__header",
      },
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__footer",
      },
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".cart-drawer__form-wrapper",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);
